import { SUIT } from "./card";

export enum BID {
    NONE = -1,
    NORMAL = 1,
    ALERTED = 2,
    PASS = 81,
    DOUBLE = 82,
    REDOUBLE = 83,
}

export default class Bid {
    suit = -1
    height = 0
    alert = false
    explanation = ""
    // when Sabi explains, we don't want to see it in the chat.
    hideExplanation = false;

    constructor(height?: number, suit?: number) {
        if (suit) {
            console.assert(suit < 10);
            this.suit = suit;
        }

        if (height) {
            console.assert(height < 10);
            this.height = height;
        }
    }

    /** 
     * Useful for cloning a Bid object
     **/
    static fromObject(obj: any | Bid) {
        const b = new Bid(obj.height, obj.suit);
        b.alert = obj.alert ?? false;
        b.explanation = obj.explanation ?? "";
        b.hideExplanation = obj.hideExplanation ?? false;
        return b;
    }

    static fromInt(serverInt: number) {
        const bid = new Bid();
        bid.setInt(serverInt);
        return bid;
    }

    static fromPass() {
        return Bid.fromInt(BID.PASS);
    }

    static fromDouble() {
        return Bid.fromInt(BID.DOUBLE);
    }

    static fromRedouble() {
        return Bid.fromInt(BID.REDOUBLE);
    }

    isValid() {
        return (this.height > 0 && this.height < 8 && this.suit >= SUIT.CLUBS && this.suit <= SUIT.NOTRUMP)
            || (this.height == 8 && this.suit >= 0 && this.suit <= 3); // specials like BID_REDOUBLE
    }

    set(b: Bid) {
        this.height = b.height;
        this.suit = b.suit;
        this.alert = b.alert;
        this.explanation = b.explanation;
    }

    setInt(i: number): boolean {
        this.alert = false;
        this.explanation = "";


        if (i >= 100) {// a full bid
            this.alert = (i % 10) === BID.ALERTED;
            i = Math.floor(i / 10);
        }

        this.height = Math.floor(i / 10);
        this.suit = i % 10

        return this.isValid();
    }

    setNone() { this.setInt(BID.NONE); }

    clone() {
        return Bid.fromObject(this);
    }

    /**
     * For communicating with the server, Bids are coded as a 2 digit number.
     * @return int of the coded Bid.
     */
    getInt() { return this.height * 10 + this.suit; }
    getFullInt() {
        return this.height * 100 + this.suit * 10 + (this.alert ? BID.ALERTED : BID.NORMAL);
    }
    /**
     * Is this Bid playable as a Contract?
     * @return true if this is playable as a contract, like 1S or 3NT
     */
    isContract() { return this.isValid() && this.getInt() < BID.PASS; }
    isPass() { return this.getInt() == BID.PASS; }
    isDouble() { return this.getInt() == BID.DOUBLE; }
    isRedouble() { return this.getInt() == BID.REDOUBLE; }

    equals(b?: Bid) {
        return b && this.height === b.height && this.suit === b.suit;
    }

    lessThan(b: Bid) {
        if (this.height === b.height)
            return this.suit < b.suit;
        else
            return this.height < b.height;
    }

    greaterThan(b: Bid) {
        if (this.height === b.height)
            return this.suit > b.suit;
        else
            return this.height > b.height;
    }

    lessOrEqual(b: Bid) {
        if (this.height === b.height)
            return this.suit <= b.suit;
        else
            return this.height < b.height;
    }

    greaterOrEqual(b: Bid) {
        if (this.height === b.height)
            return this.suit >= b.suit;
        else
            return this.height > b.height;
    }

    getPrevious() {
        const ret = Bid.fromObject(this);

        if (ret.suit >= SUIT.CLUBS) {
            if (ret.suit === SUIT.CLUBS) {
                if (this.height > 1) {
                    ret.height--;
                    ret.suit = SUIT.NOTRUMP;
                } else {
                    ret.height = ret.suit = 0;
                }
            } else {
                ret.suit--;
            }
        }
        return ret;
    }

    getNext() {
        const ret = Bid.fromObject(this);

        if (ret.suit >= SUIT.CLUBS) {
            if (ret.suit === SUIT.NOTRUMP) {
                if (ret.height < 8) {
                    ret.height++;
                    ret.suit = SUIT.CLUBS;
                } else {
                    ret.height = ret.suit = 0;
                }
            } else {
                ret.suit++;
            }
        }
        return ret;
    }

    toString(): string {
        let s = this.toStringWithoutAlert();

        if (this.alert)
            s += "A";

        return s;
    }

    toStringWithoutAlert(): string {
        let s = "-";

        if (this.suit > -1) {
            if (this.height < 8) {
                s = "" + this.height + "-CDHSN"[this.suit];
            } else {
                switch (this.getInt()) {
                    case BID.PASS: s = "P"; break;
                    case BID.DOUBLE: s = "X"; break;
                    case BID.REDOUBLE: s = "XX"; break;
                }
            }
        }
        return s;
    }

    fromString(s: string) {
        if (s.startsWith("XX") || s.startsWith("R")) {
            this.setInt(BID.REDOUBLE)
        } else if (s.startsWith("X")) {
            this.setInt(BID.DOUBLE)
        } else if (s.startsWith("P")) {
            this.setInt(BID.PASS)
        } else {
            this.height = Number(s[0])
            this.suit = "-CDHSN".indexOf(s[1])
        }

        this.alert = s.endsWith("A")
    }
}

/*
 north=T8.A87542.96.T96
 east=J754.6.Q754.AK85
 south=Q632.KT.KJ32.QJ2
 west=AK9.QJ93.AT8.743
 contract=4SX
 
 declarer=E
 lead=D2
 bidding=PP1D1HP1SP2SP3SP4SPPXPPP
 play=D2D8D9DQS4S2SAS8C3C6CAC2SJS3S9STS5S6SKD6HQH2H6HKHTHJHAS7D4DJDTH4SQH3C9D5CQC4CTCKD7D3DAH5C7H7C8CJDKH9H8C5&tricks=8&score=300
 */